// Corrections des ventes validées (14/10/2026) — le client voit l'aperçu des
// écritures puis confirme lui-même ; rien n'est comptabilisé sans sa confirmation.
//  - Ventes figées « sur les débits » après le passage sur les encaissements :
//    TVA mise en attente (445740) jusqu'au règlement.
//  - Ventes comptabilisées sur un compte de vente provisoire (706000) avant le
//    mode « plan » : reclassement vers les comptes du journal des ventes Dolibarr.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const frDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

function EntriesTable({ lines }) {
  return (
    <table className="table" style={{ width: "100%", fontSize: 12, marginBottom: 6 }}>
      <thead><tr><th>Date</th><th>Journal</th><th>Pièce</th><th>Compte</th><th>Libellé</th><th style={{ textAlign: "right" }}>Débit</th><th style={{ textAlign: "right" }}>Crédit</th></tr></thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={i}><td>{frDate(l.date)}</td><td>{l.journal}</td><td>{l.piece}</td><td>{l.account_code}</td><td>{l.label}</td>
            <td style={{ textAlign: "right" }}>{l.debit ? eur(l.debit) : ""}</td><td style={{ textAlign: "right" }}>{l.credit ? eur(l.credit) : ""}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 1000, width: "95%", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="modal-header"><h3 className="modal-title">{title}</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        <div style={{ padding: 16, fontSize: 13 }}>{children}</div>
      </div>
    </div>
  );
}

// ── TVA sur les encaissements : ventes validées avant le changement de régime ─
function ExigibilityModal({ companyId, onClose }) {
  const [list, setList] = useState(null);
  const [picked, setPicked] = useState([]);
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    api.salesCorrections.exigibilityList(companyId)
      .then(l => { setList(l); setPicked((l.invoices || []).filter(i => i.reason === null).map(i => i.id)); })
      .catch(e => setError(e.message));
  }, [companyId]);
  const eligible = (list?.invoices || []).filter(i => i.reason === null);
  const warnings = [...new Set(eligible.filter(i => picked.includes(i.id) && i.warning).map(i => i.warning))];
  const toggle = (id) => { setPreview(null); setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id])); };
  const run = async (apply) => {
    setBusy(true); setError("");
    try {
      if (apply) {
        if (!window.confirm(`Comptabiliser les écritures de ${picked.length} facture(s) ? Elles seront tracées à votre nom au journal d'audit.`)) return;
        setDone(await api.salesCorrections.exigibilityApply(companyId, picked)); setPreview(null);
      } else setPreview(await api.salesCorrections.exigibilityPreview(companyId, picked));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title="🧾 Ventes validées avant le passage sur les encaissements" onClose={onClose}>
      <p style={{ marginTop: 0, color: "#4b5563" }}>
        Ces factures de prestations ont été validées quand la société était « sur les débits » : leur TVA a été comptée exigible à la date de la facture.
        Sur les encaissements, elle n'est due qu'au règlement. Toutes les factures proposées sont cochées : chacune est traitée selon son paiement réel —
        TVA due à la date du règlement s'il est connu (relevé bancaire validé), sinon mise en attente jusqu'au règlement. Affichez les écritures, puis confirmez.
      </p>
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {done ? (
        <div style={{ background: "#f0fdf9", padding: 12, borderRadius: 8 }}>
          ✓ Écritures comptabilisées pour {done.invoices.length} facture(s) : {eur(done.totals.moved_to_pending)} mis en attente d'encaissement,
          {" "}{eur(done.totals.transferred_on_payments)} déjà exigibles (règlements connus), {eur(done.totals.pending_after)} en attente de règlement.
          <div style={{ marginTop: 8 }}><button className="btn" onClick={onClose}>Fermer</button></div>
        </div>
      ) : !list ? <div>Chargement…</div> : list.vat_exigibility !== "encaissements" ? (
        <div style={{ color: "#b45309" }}>Votre société est « sur les débits » : rien à corriger. Si votre expert-comptable confirme les encaissements, changez d'abord le réglage dans Groupe &gt; Sociétés &gt; ✏️ Modifier &gt; « Exigibilité de la TVA ».</div>
      ) : (
        <>
          <table className="table" style={{ width: "100%", fontSize: 12 }}>
            <thead><tr><th></th><th>Facture</th><th>Date</th><th style={{ textAlign: "right" }}>TTC</th><th style={{ textAlign: "right" }}>TVA</th><th style={{ textAlign: "right" }}>Règlements connus</th><th>État</th></tr></thead>
            <tbody>
              {list.invoices.map(i => (
                <tr key={i.id} style={{ opacity: i.reason ? 0.55 : 1 }}>
                  <td>{i.reason === null && <input type="checkbox" checked={picked.includes(i.id)} onChange={() => toggle(i.id)} />}</td>
                  <td>{i.number}</td><td>{frDate(i.date)}</td><td style={{ textAlign: "right" }}>{eur(i.ttc_amount)}</td><td style={{ textAlign: "right" }}>{eur(i.tva_amount)}</td>
                  <td style={{ textAlign: "right" }}>{i.paid_known ? eur(i.paid_known) : "—"}</td><td>{i.reason || (i.warning ? <span style={{ color: "#b45309" }}>⚠️ aucun règlement connu — {i.warning}</span> : i.paid_known ? "TVA due au règlement connu" : "TVA mise en attente jusqu'au règlement")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!eligible.length && <div style={{ color: "#4b5563" }}>Aucune facture à corriger.</div>}
          {warnings.length > 0 && (
            <div role="alert" style={{ background: "#f59e0b22", color: "#b45309", padding: "8px 12px", borderRadius: 8, marginTop: 8 }}>
              ⚠️ {warnings.join(" ")} Certaines factures cochées n'ont aucun règlement connu : si elles ont été payées, validez d'abord les règlements de votre relevé (Trésorerie &amp; banque &gt; Comptes bancaires), sinon leur TVA sera mise en attente à tort.
              Les paiements par carte reçus par Stripe ne sont pas encore rapprochables des factures (fonction en préparation).
            </div>
          )}
          {eligible.length > 0 && <button className="btn sm" style={{ marginTop: 8, marginRight: 8 }} onClick={() => { setPreview(null); setPicked(picked.length === eligible.length ? [] : eligible.map(i => i.id)); }}>{picked.length === eligible.length ? "Tout décocher" : "Tout cocher"}</button>}
          <button className="btn" style={{ marginTop: 8 }} disabled={busy || !picked.length} onClick={() => run(false)}>👁 Voir les écritures ({picked.length})</button>
          {preview && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Écritures proposées — rien n'est encore comptabilisé</div>
              {preview.invoices.map(p => (
                <div key={p.id} style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600 }}>{p.number} — TVA mise en attente {eur(p.moved_to_pending)}{p.transferred_on_payments ? `, dont ${eur(p.transferred_on_payments)} exigibles aux règlements déjà connus` : ""}</div>
                  <EntriesTable lines={[...p.od, ...p.transfers.flatMap(t => t.lines)]} />
                </div>
              ))}
              <div>Total : {eur(preview.totals.moved_to_pending)} mis en attente (445740), {eur(preview.totals.transferred_on_payments)} exigibles aux règlements connus, {eur(preview.totals.pending_after)} en attente de règlement.</div>
              <button className="btn primary" style={{ marginTop: 8 }} disabled={busy} onClick={() => run(true)}>✓ Confirmer et comptabiliser</button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ── Reclassement des ventes comptabilisées sur un compte provisoire ─────────
function ReclassModal({ companyId, onClose }) {
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [picked, setPicked] = useState([]);
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api.salesCorrections.reclassAnalyse(companyId, null).then(setAnalysis).catch(e => setError(e.message)); }, [companyId]);
  const choose = async (f) => {
    if (!f) return;
    setBusy(true); setError(""); setPreview(null);
    try {
      const payload = { filename: f.name, content_base64: await toBase64(f) };
      const a = await api.salesCorrections.reclassAnalyse(companyId, payload);
      setFile(payload); setAnalysis(a);
      setPicked(a.invoices.filter(i => !i.reason && (i.revenue_lines.length || i.vat_lines.length)).map(i => i.id));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const run = async (apply) => {
    setBusy(true); setError("");
    try {
      if (apply) {
        if (!window.confirm(`Comptabiliser le reclassement de ${picked.length} facture(s) ? Il sera tracé à votre nom au journal d'audit.`)) return;
        setDone(await api.salesCorrections.reclassApply(companyId, file, picked)); setPreview(null);
      } else setPreview(await api.salesCorrections.reclassPreview(companyId, file, picked));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const ready = (i) => !i.reason && (i.revenue_lines.length || i.vat_lines.length);
  return (
    <Modal title="📒 Ventes sur un compte de vente provisoire" onClose={onClose}>
      <p style={{ marginTop: 0, color: "#4b5563" }}>
        Ces factures ont été validées avant l'utilisation des comptes de vente de votre plan : leur chiffre d'affaires est sur un compte provisoire
        (et leur TVA sur un compte unique). Choisissez le journal des ventes du mois exporté de Dolibarr : il indique le bon compte de chaque facture.
        Le fichier n'est pas conservé. Vérifiez les écritures proposées, puis confirmez.
      </p>
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {done ? (
        <div style={{ background: "#f0fdf9", padding: 12, borderRadius: 8 }}>
          ✓ Reclassement comptabilisé pour {done.invoices.length} facture(s) : {eur(done.totals.revenue_moved)} de chiffre d'affaires, {eur(done.totals.vat_moved)} de TVA.
          <div style={{ marginTop: 8 }}><button className="btn" onClick={onClose}>Fermer</button></div>
        </div>
      ) : !analysis ? <div>Chargement…</div> : !analysis.invoices.length ? <div>Aucune facture sur un compte provisoire.</div> : (
        <>
          <label className="btn primary" style={{ cursor: busy ? "wait" : "pointer", marginBottom: 10 }}>
            {busy && !preview ? "Lecture…" : file ? `Journal : ${file.filename} — changer` : "Choisir le journal des ventes Dolibarr"}
            <input type="file" accept=".csv,.txt,.xlsx" style={{ display: "none" }} disabled={busy} onChange={e => { choose(e.target.files[0]); e.target.value = ""; }} />
          </label>
          <table className="table" style={{ width: "100%", fontSize: 12 }}>
            <thead><tr><th></th><th>Facture</th><th>Date</th><th style={{ textAlign: "right" }}>HT</th><th>Compte(s) actuel(s)</th><th>Proposition</th></tr></thead>
            <tbody>
              {analysis.invoices.map(i => (
                <tr key={i.id} style={{ opacity: file && !ready(i) ? 0.55 : 1 }}>
                  <td>{file && ready(i) && <input type="checkbox" checked={picked.includes(i.id)} onChange={() => { setPreview(null); setPicked(p => (p.includes(i.id) ? p.filter(x => x !== i.id) : [...p, i.id])); }} />}</td>
                  <td>{i.number}</td><td>{frDate(i.date)}</td><td style={{ textAlign: "right" }}>{eur(i.ht_amount)}</td>
                  <td>{i.provisional_accounts.join(", ")}</td>
                  <td>{!file ? "journal à choisir" : i.reason ? <span style={{ color: "#b45309" }}>{i.reason}</span>
                    : <>{i.revenue_lines.filter(l => l.delta > 0).map(l => `${l.account_code} ${eur(l.delta)}`).join(" · ")}{i.vat_lines.length ? ` · TVA ${i.vat_lines.filter(l => l.delta > 0).map(l => `${l.account_code} ${eur(l.delta)}`).join(" · ")}` : ""}{i.vat_note ? <span style={{ color: "#4b5563" }}> ({i.vat_note})</span> : null}</>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {file && <button className="btn" style={{ marginTop: 8 }} disabled={busy || !picked.length} onClick={() => run(false)}>👁 Voir les écritures ({picked.length})</button>}
          {preview && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Écritures proposées — rien n'est encore comptabilisé</div>
              {preview.invoices.map(p => <div key={p.id}><div style={{ fontWeight: 600 }}>{p.number}</div><EntriesTable lines={p.lines} /></div>)}
              <div>Total : {eur(preview.totals.revenue_moved)} de chiffre d'affaires et {eur(preview.totals.vat_moved)} de TVA reclassés.</div>
              <button className="btn primary" style={{ marginTop: 8 }} disabled={busy} onClick={() => run(true)}>✓ Confirmer et comptabiliser</button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/** Bandeau de la page Facturation : n'apparaît que s'il y a des ventes à corriger. */
export default function SalesCorrections({ companyId, onChanged }) {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { if (companyId) api.salesCorrections.summary(companyId).then(setS).catch(() => setS(null)); }, [companyId]);
  useEffect(() => { load(); }, [load]);
  if (!s || (!s.exigibility_to_fix && !s.reclass_to_do)) return null;
  const close = () => { setOpen(null); load(); onChanged?.(); };
  return (
    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
      <span>🛠 Corrections proposées sur des ventes déjà validées :</span>
      {s.exigibility_to_fix > 0 && <button className="btn sm" onClick={() => setOpen("exig")}>TVA sur les encaissements — {s.exigibility_to_fix} facture(s) validée(s) avant le changement</button>}
      {s.reclass_to_do > 0 && <button className="btn sm" onClick={() => setOpen("reclass")}>Comptes de vente — {s.reclass_to_do} facture(s) sur un compte provisoire</button>}
      {open === "exig" && <ExigibilityModal companyId={companyId} onClose={close} />}
      {open === "reclass" && <ReclassModal companyId={companyId} onClose={close} />}
    </div>
  );
}
